import { lazy, Suspense, useMemo, useRef, useState } from 'react';
// Type-only: erased at runtime, so this does NOT pull the vega chunk eagerly.
import type { CustomChartGrammar } from '@erp/viz/custom';
import { T, useGT } from 'gt-react';
import {
  BracketsCurlyIcon,
  CaretRightIcon,
  SparkleIcon,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Textarea } from '@/components/ui/textarea';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { useGenerateVegaSpec } from '@/api/rpc/hooks/core/useGenerateVegaSpec';
import { defineComponentType, type ComponentRegistry } from '../registry';
import { useReportColors } from '../palette/ReportColorPaletteContext';
import {
  useComponentDataset,
  useReportDataset,
  type ReportDataset,
  type ReportDatasetRow,
} from '../ReportDataContext';
import { useIsFramed } from '../ComponentFrame';
import { cn } from '@/lib/utils';
import type { BaseComponentConfig } from '../types';
import {
  ChartEmptyState,
  ConfigRow,
  DatasetSelectRow,
  ChartTooSmall,
  useChartFit,
} from '../charts/shared';
import { useElementHeight, useElementSize } from '../charts/useElementSize';
import { starterSpecFor } from './starterSpec';
import { stableCategoryDomains } from './stableColors';
import {
  generateValidatedChart,
  parseCustomSpecDraft,
  validateCustomSpec,
} from './validateCustomSpec';

// ── Custom chart widget ───────────────────────────────────────────────────────
// A canvas component whose entire behavior is data: the config carries a
// Vega-Lite or full Vega spec as plain JSON, so new visualizations can be
// authored (by a user in the config form, or generated on the fly by an agent)
// without shipping new component code. The bound dataset's rows are injected
// at render time — specs never embed data (see `bindDataset` in
// @erp/viz/custom for the contract).

export interface VegaChartWidgetConfig extends BaseComponentConfig {
  type: 'vega-chart';
  /**
   * The grammar `spec` is written in. Configs saved before full Vega was
   * supported have none; hydration fills 'vega-lite' from `make()`.
   */
  grammar: CustomChartGrammar;
  /** The spec as plain JSON; null until configured. */
  spec: Record<string, unknown> | null;
  /**
   * The plot's height in pixels, for a chart with a natural height (a list
   * of bars); absent = the plot fills its cell.
   */
  plotHeight?: number;
}

// The vega stack is heavy (~1.5 MB minified); load it only when a canvas
// actually contains a vega component.
const VegaChartInner = lazy(() =>
  import('./VegaChartInner').then((m) => ({ default: m.VegaChartInner })),
);

/**
 * What a custom chart's cell shows: the empty state until it has a spec and a
 * dataset, a "too small" note — decided before the vega chunk loads, so a
 * cell too small to draw in never pays for it — or the chart.
 */
function customChartState(
  spec: Record<string, unknown> | null,
  hasDataset: boolean,
  drawable: boolean,
): 'empty' | 'too-small' | 'chart' {
  if (!spec || !hasDataset) return 'empty';
  return drawable ? 'chart' : 'too-small';
}

/**
 * The plot's height in its measured wrapper, less the wrapper's padding so it
 * fits without scrolling; before the first measure, the canvas row baseline.
 */
function plotHeight(measured: number): number {
  return measured > 0 ? measured - 16 : 280;
}

/** The spec with each category's color keyed to it (see stableCategoryDomains). */
function stableSpec(
  grammar: CustomChartGrammar,
  spec: Record<string, unknown> | null,
  allRows: readonly ReportDatasetRow[] | undefined,
): Record<string, unknown> | null {
  return spec ? stableCategoryDomains(grammar, spec, allRows ?? []) : null;
}

/**
 * A custom chart's rows, grammar and spec, each category keeping its color
 * whatever the Report filters leave.
 */
function useStableCustomSpec(
  component: VegaChartWidgetConfig,
  dataset: ReportDataset | null,
) {
  const rows = dataset?.rows ?? EMPTY_ROWS;
  const grammar = component.grammar ?? 'vega-lite';
  const allRows = dataset?.allRows ?? dataset?.rows;
  const spec = useMemo(
    () => stableSpec(grammar, component.spec, allRows),
    [grammar, component.spec, allRows],
  );
  return { rows, grammar, spec };
}

const EMPTY_ROWS: ReportDatasetRow[] = [];

/** The least height a framed chart without a natural height asks its cell for. */
const MIN_FIT_HEIGHT = 150;
const MIN_PLOT_HEIGHT = 60;

/**
 * A chart in a component frame, which grows its cell to fit its content: a
 * chart with a natural height (`plotHeight`) keeps it, the spare room below
 * it; one without draws at the height it gets, at least a readable one.
 */
function FramedVegaChart({ component }: { component: VegaChartWidgetConfig }) {
  const dataset = useComponentDataset(component);
  const colors = useReportColors();
  const plotRef = useRef<HTMLDivElement>(null);
  const size = useElementSize(plotRef);
  const { rows, grammar, spec } = useStableCustomSpec(component, dataset);
  const fixed = rows.length > 0 ? component.plotHeight : undefined;
  return (
    <div
      ref={plotRef}
      data-fit-min={fixed ?? MIN_FIT_HEIGHT}
      className={cn('min-w-0', fixed ? 'shrink-0' : 'min-h-0 flex-1')}
      style={fixed ? { height: fixed } : undefined}
    >
      {!spec || !dataset ? (
        <ChartEmptyState
          hasData={rows.length > 0}
          icon={
            <BracketsCurlyIcon className="h-8 w-8 text-muted-foreground/50" />
          }
        />
      ) : (
        size.width > 0 && (
          <Suspense fallback={null}>
            <VegaChartInner
              grammar={grammar}
              spec={spec}
              rows={rows}
              palette={colors}
              width={size.width}
              height={fixed ?? Math.max(MIN_PLOT_HEIGHT, size.height)}
            />
          </Suspense>
        )
      )}
    </div>
  );
}

function VegaChartRenderer({
  component,
}: {
  component: VegaChartWidgetConfig;
}) {
  const framed = useIsFramed();
  if (framed) return <FramedVegaChart component={component} />;
  return <PlainVegaChart component={component} />;
}

function PlainVegaChart({ component }: { component: VegaChartWidgetConfig }) {
  const dataset = useComponentDataset(component);
  const colors = useReportColors();
  const containerRef = useRef<HTMLDivElement>(null);
  const measuredHeight = useElementHeight(containerRef);
  const { width, detail } = useChartFit();
  const { rows, grammar, spec } = useStableCustomSpec(component, dataset);
  const state = customChartState(spec, dataset !== null, detail.drawable);

  // The measured wrapper is always mounted: the height hook observes it from
  // the first render, so a chart given its first spec (Apply on an empty
  // chart) is sized to its cell rather than stuck at the fallback height,
  // which clipped its x axis.
  return (
    <div
      ref={containerRef}
      className={
        state === 'chart'
          ? 'min-h-0 min-w-0 flex-1 p-2'
          : 'flex min-h-0 min-w-0 flex-1 flex-col'
      }
    >
      {state === 'empty' && (
        <ChartEmptyState
          hasData={rows.length > 0}
          icon={
            <BracketsCurlyIcon className="h-8 w-8 text-muted-foreground/50" />
          }
        />
      )}
      {state === 'too-small' && <ChartTooSmall />}
      {state === 'chart' && spec && (
        <Suspense fallback={null}>
          <VegaChartInner
            grammar={grammar}
            spec={spec}
            rows={rows}
            palette={colors}
            height={plotHeight(measuredHeight)}
            width={width}
          />
        </Suspense>
      )}
    </div>
  );
}

/**
 * Validates a spec for its grammar — the same gate the config form applies,
 * plus a render against `rows` when given. Returns null when valid, else the
 * reasons. Exported for the whole-canvas generation pipeline
 * (canvasGeneration.ts) so specs inside generated layouts get exactly the same
 * validation.
 */
export async function validateSpecObject(
  spec: Record<string, unknown>,
  grammar: CustomChartGrammar = 'vega-lite',
  rows?: readonly ReportDatasetRow[],
): Promise<string | null> {
  return (await validateCustomSpec(grammar, spec, rows)).error;
}

const GRAMMARS = [
  'vega-lite',
  'vega',
] as const satisfies readonly CustomChartGrammar[];

// Proper names of the two grammars — not translated.
const GRAMMAR_LABELS: Record<CustomChartGrammar, string> = {
  'vega-lite': 'Vega-Lite',
  vega: 'Vega',
};

function VegaChartConfigForm({
  component,
  onUpdate,
}: {
  component: VegaChartWidgetConfig;
  onUpdate: (updated: VegaChartWidgetConfig) => void;
}) {
  const gt = useGT();
  const dataset = useReportDataset(component.datasetKey);
  const [draft, setDraft] = useState(() =>
    component.spec ? JSON.stringify(component.spec, null, 2) : '',
  );
  // The grammar the draft is written in; applied together with it.
  const [draftGrammar, setDraftGrammar] = useState<CustomChartGrammar>(
    component.grammar ?? 'vega-lite',
  );
  // Why the last AI generation was refused (its reasons live under Advanced)…
  const [generateError, setGenerateError] = useState<string | null>(null);
  // …and why the last manual Apply was, shown inline where it was applied.
  const [applyError, setApplyError] = useState<string | null>(null);
  // What the render proof could not settle about an applied spec.
  const [applyWarning, setApplyWarning] = useState<string | null>(null);
  const [describe, setDescribe] = useState('');
  const [generating, setGenerating] = useState(false);
  // The JSON editor is a power-user escape hatch, folded away by default;
  // opened by the author.
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const generateSpec = useGenerateVegaSpec();

  const starter = useMemo(
    () => starterSpecFor(draftGrammar, dataset?.columns ?? [], dataset?.rows),
    [draftGrammar, dataset?.columns, dataset?.rows],
  );

  const applyDraft = async () => {
    const result = await parseCustomSpecDraft(
      draftGrammar,
      draft,
      dataset?.rows,
    );
    setApplyError(result.error);
    setApplyWarning(result.spec ? result.warnings.join('\n') || null : null);
    setGenerateError(null);
    if (result.spec) {
      onUpdate({ ...component, grammar: draftGrammar, spec: result.spec });
    }
  };

  const generateFromDescription = async () => {
    if (!dataset || !describe.trim()) return;
    const request = {
      prompt: describe.trim(),
      datasetLabel: dataset.label,
      columns: dataset.columns,
      sampleRows: dataset.rows.slice(0, 5),
      currentSpec: component.spec ?? undefined,
      currentGrammar: component.spec ? component.grammar : undefined,
    };
    setGenerating(true);
    setGenerateError(null);
    setApplyError(null);
    try {
      const { chart, error: refused } = await generateValidatedChart(
        generateSpec.mutateAsync,
        request,
        dataset.rows,
      );
      if (!chart) {
        setGenerateError(refused);
        return;
      }
      // Validated — fill the editor and apply immediately so the chart
      // updates behind the popover.
      setDraft(JSON.stringify(chart.spec, null, 2));
      setDraftGrammar(chart.grammar);
      onUpdate({ ...component, grammar: chart.grammar, spec: chart.spec });
    } catch (err) {
      setGenerateError(err instanceof Error ? err.message : String(err));
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="space-y-4">
      <DatasetSelectRow
        component={component}
        onDatasetChange={(datasetKey) => onUpdate({ ...component, datasetKey })}
      />
      <ConfigRow label={gt('Describe your chart')} htmlFor="vega-describe">
        <Textarea
          id="vega-describe"
          value={describe}
          onChange={(e) => setDescribe(e.target.value)}
          placeholder={gt(
            'e.g. Stacked bar of open balance by aging bucket, one series per customer',
          )}
          className="min-h-16 text-xs"
        />
      </ConfigRow>
      <Button
        type="button"
        size="sm"
        onClick={() => void generateFromDescription()}
        disabled={generating || !describe.trim() || !dataset}
      >
        <SparkleIcon className="h-3.5 w-3.5" />
        {generating ? gt('Generating…') : gt('Generate chart')}
      </Button>
      {generateError && (
        <p className="text-xs text-destructive">
          <T>
            Couldn't build that chart — try rephrasing, or check Advanced for
            details.
          </T>
        </p>
      )}
      {dataset && dataset.columns.length > 0 && (
        <p className="text-xs text-muted-foreground">
          {gt('Available fields')}:{' '}
          {dataset.columns.map((c) => c.key).join(', ')}
        </p>
      )}
      <Collapsible open={advancedOpen} onOpenChange={setAdvancedOpen}>
        <CollapsibleTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 gap-1 px-1 text-xs text-muted-foreground"
          >
            <CaretRightIcon
              className={`h-3 w-3 transition-transform ${advancedOpen ? 'rotate-90' : ''}`}
            />
            <T>Advanced: spec JSON</T>
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent className="space-y-3 pt-2">
          <ToggleGroup
            type="single"
            value={draftGrammar}
            aria-label={gt('Spec grammar')}
            onValueChange={(next) => {
              // Radix emits '' when the active item is re-clicked; ignore.
              if (next === 'vega-lite' || next === 'vega') {
                setDraftGrammar(next);
                setApplyError(null);
              }
            }}
          >
            {GRAMMARS.map((grammar) => (
              <ToggleGroupItem
                key={grammar}
                value={grammar}
                className="h-7 px-2 text-xs"
              >
                {GRAMMAR_LABELS[grammar]}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
          <Textarea
            id="vega-spec-draft"
            aria-label={gt('Spec JSON')}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={
              draftGrammar === 'vega'
                ? gt(
                    'Paste a Vega spec — read the selected data source from the "table" data',
                  )
                : gt(
                    'Paste a Vega-Lite spec — the selected data source is injected automatically',
                  )
            }
            className="min-h-40 font-mono text-xs"
            spellCheck={false}
          />
          {applyWarning && (
            <p className="whitespace-pre-wrap break-words text-xs text-muted-foreground">
              {applyWarning}
            </p>
          )}
          {(applyError ?? generateError) && (
            <p
              role="alert"
              className="whitespace-pre-wrap break-words text-xs text-destructive"
            >
              {applyError ?? generateError}
            </p>
          )}
          <div className="flex gap-2">
            <Button type="button" size="sm" onClick={() => void applyDraft()}>
              {gt('Apply spec')}
            </Button>
            {starter && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => {
                  setDraft(JSON.stringify(starter, null, 2));
                  setApplyError(null);
                }}
              >
                {gt('Insert starter spec')}
              </Button>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

function VegaChartPreview() {
  return (
    <div className="flex h-24 items-center justify-center text-foreground">
      <svg viewBox="0 0 44 36" className="h-16 w-auto" fill="none">
        <path
          d="M13 8c-3 0-3 4-3 6s0 4-3 4c3 0 3 2 3 4s0 6 3 6"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeOpacity="0.5"
          strokeLinecap="round"
        />
        <path
          d="M31 8c3 0 3 4 3 6s0 4 3 4c-3 0-3 2-3 4s0 6-3 6"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeOpacity="0.5"
          strokeLinecap="round"
        />
        <rect
          x="17"
          y="19"
          width="3.5"
          height="8"
          rx="1"
          fill="currentColor"
          fillOpacity="0.85"
        />
        <rect
          x="23"
          y="13"
          width="3.5"
          height="14"
          rx="1"
          fill="currentColor"
          fillOpacity="0.55"
        />
      </svg>
    </div>
  );
}

const vegaChartDefinition = defineComponentType<VegaChartWidgetConfig>({
  type: 'vega-chart',
  label: 'Custom chart',
  defaultTitle: () => 'Custom chart',
  description:
    'Dataset-bound escape hatch for anything the other chart types cannot express. "grammar" is "vega-lite" (prefer it) or "vega" (full Vega, for hierarchy/network layouts or custom signals); "spec" carries a spec in that grammar. Never include $schema/width/height/autosize/config/inline data/url — the host injects the bound dataset (a Vega spec reads it from the data source named "table") and its theme. "plotHeight" (px) fixes the plot height of a chart with a natural one, such as a list of bars. Prefer builtin chart types when they fit.',
  version: 1,
  make: (id) => ({ type: 'vega-chart', id, grammar: 'vega-lite', spec: null }),
  generatesInConfigForm: true,
  Renderer: VegaChartRenderer,
  ConfigForm: VegaChartConfigForm,
  Preview: VegaChartPreview,
});

// ── Registry fragment ─────────────────────────────────────────────────────────
// Opt-in per surface. Deliberately NOT part of builtinChartRegistry: the query
// builder persists layouts through a closed-union schema that would reject a
// vega component, so only surfaces using the open canvas document format
// (e.g. the AR analytics sections) should spread this in.

export const vegaWidgetRegistry: ComponentRegistry = {
  [vegaChartDefinition.type]: vegaChartDefinition,
};
